import Phaser from "phaser";
import { RUN_CAMPEON_SONG_SYNC_CONFIG } from "../songSync/runCampeonSongSyncConfig";
import type { GamePhase, ScoreResult } from "../types";
import { getWolfRank, WOLF_RANK_SCORE } from "../wolfRankConfig";
import type { RhythmJudgement } from "./RhythmRewardSystem";

const ACTIVE_SYNC_EVENTS = RUN_CAMPEON_SONG_SYNC_CONFIG.predictedEventsEnabled
  ? [...RUN_CAMPEON_SONG_SYNC_CONFIG.events, ...RUN_CAMPEON_SONG_SYNC_CONFIG.predictedEvents]
  : RUN_CAMPEON_SONG_SYNC_CONFIG.events;

const TOTAL_SYNC_ISOTYPES = ACTIVE_SYNC_EVENTS.reduce((total, event) => total + Math.max(1, event.count), 0);
const MAJOR_SYNC_EVENTS = ACTIVE_SYNC_EVENTS.filter((event) => event.intensity === "high" || event.intensity === "max");

export class ScoreSystem {
  private pickups = 0;
  private collectedIsotypes = 0;
  private collectedMajorPickups = 0;
  private collectedMajorEventIds = new Set<string>();
  private championActivations = 0;
  private championSeconds = 0;
  private pressureSeconds = 0;
  private songAliveSeconds = 0;
  private rhythmPerfect = 0;
  private rhythmGood = 0;
  private rhythmMiss = 0;
  private beatPickupCount = 0;
  private beatSyncWeight = 0;
  private bestCombo = 0;
  private hits = 0;
  private comboBreaks = 0;
  private offBeatPickups = 0;
  private finalized?: ScoreResult;

  reset() {
    this.pickups = 0;
    this.collectedIsotypes = 0;
    this.collectedMajorPickups = 0;
    this.collectedMajorEventIds.clear();
    this.championActivations = 0;
    this.championSeconds = 0;
    this.pressureSeconds = 0;
    this.songAliveSeconds = 0;
    this.rhythmPerfect = 0;
    this.rhythmGood = 0;
    this.rhythmMiss = 0;
    this.beatPickupCount = 0;
    this.beatSyncWeight = 0;
    this.bestCombo = 0;
    this.hits = 0;
    this.comboBreaks = 0;
    this.offBeatPickups = 0;
    this.finalized = undefined;
  }

  update(dt: number, danger: number, isChampion: boolean, songEnergyRatio = 0) {
    if (isChampion) this.championSeconds += dt;
    if (danger > 0.58) this.pressureSeconds += dt * Phaser.Math.Clamp((danger - 0.58) / 0.42, 0, 1);
    if (songEnergyRatio > 0.04) this.songAliveSeconds += dt;
  }

  addPickup(_scoreValue = 1, rhythm?: RhythmJudgement, pickupMeta?: { songSyncEventId?: string; intensity?: string; targetCounterTime?: number }) {
    this.pickups += 1;
    if (pickupMeta?.targetCounterTime !== undefined) this.collectedIsotypes += 1;
    if (pickupMeta?.songSyncEventId && this.isMajorPickup(pickupMeta.intensity)) {
      this.collectedMajorPickups += 1;
      this.collectedMajorEventIds.add(pickupMeta.songSyncEventId);
    }
    if (!rhythm || rhythm.id === "none") return;

    this.beatPickupCount += 1;
    const weight = this.getBeatWeight(Math.abs(rhythm.delta));
    this.beatSyncWeight += weight;
    if (rhythm.id === "perfect") this.rhythmPerfect += 1;
    else if (rhythm.id === "good") this.rhythmGood += 1;
    else if (rhythm.id === "miss") this.rhythmMiss += 1;
    if (weight <= 0) this.offBeatPickups += 1;
    if (rhythm.comboBreak) this.comboBreaks += 1;
    this.bestCombo = Math.max(this.bestCombo, rhythm.combo);
  }

  addChampionActivation() {
    this.championActivations += 1;
  }

  addHit() {
    this.hits += 1;
  }

  calculate(elapsed: number, distance: number, phase: GamePhase, songEnergyRatio = 0): ScoreResult {
    if (this.finalized) return this.finalized;
    const survivalSeconds = Phaser.Math.Clamp(elapsed, 0, WOLF_RANK_SCORE.gameDurationSeconds);
    const finishedRun = phase === "victory";
    const survivalRatio = Phaser.Math.Clamp(survivalSeconds / WOLF_RANK_SCORE.gameDurationSeconds, 0, 1);
    const isotypeRatio = TOTAL_SYNC_ISOTYPES > 0 ? Phaser.Math.Clamp(this.collectedIsotypes / TOTAL_SYNC_ISOTYPES, 0, 1) : 0;
    const beatSyncRatio = this.beatPickupCount > 0 ? Phaser.Math.Clamp(this.beatSyncWeight / this.beatPickupCount, 0, 1) : 0;
    const flowRatio = Phaser.Math.Clamp(this.bestCombo / 14, 0, 1);
    const pressureRatio = Phaser.Math.Clamp(this.pressureSeconds / 20, 0, 1);

    const survivalScore = Math.round(WOLF_RANK_SCORE.survivalMax * survivalRatio);
    const isotypeScore = Math.round(WOLF_RANK_SCORE.isotypeMax * isotypeRatio);
    const beatSyncScore = Math.round(WOLF_RANK_SCORE.beatSyncMax * beatSyncRatio);
    const flowScore = Math.round(WOLF_RANK_SCORE.flowMax * flowRatio);
    const pressureScore = Math.round(WOLF_RANK_SCORE.pressureMax * pressureRatio);
    const finishScore = finishedRun ? WOLF_RANK_SCORE.finishBonus : 0;
    const penaltyScore = this.calculatePenaltyScore(elapsed);
    const total = Math.round(
      Phaser.Math.Clamp(
        survivalScore + isotypeScore + beatSyncScore + flowScore + pressureScore + finishScore - penaltyScore,
        0,
        WOLF_RANK_SCORE.maxScore
      )
    );
    const collectedIsotypePercent = Math.round(isotypeRatio * 1000) / 10;
    const beatSyncPercent = Math.round(beatSyncRatio * 1000) / 10;
    const missedMajorPickups = this.calculateMissedMajorPickups(elapsed, finishedRun);
    const rankResult = getWolfRank(total, {
      finishedRun,
      survivalSeconds,
      beatSyncPercent,
      collectedIsotypePercent,
      maxCombo: this.bestCombo,
      hits: this.hits,
      missedMajorPickups
    });

    return {
      total,
      elapsed,
      distance,
      pickups: this.pickups,
      championActivations: this.championActivations,
      championSeconds: this.championSeconds,
      dangerSeconds: this.pressureSeconds,
      songAliveSeconds: this.songAliveSeconds,
      songEnergyScore: beatSyncScore,
      songCompleted: finishedRun && songEnergyRatio >= 0.22,
      rhythmPerfect: this.rhythmPerfect,
      rhythmGood: this.rhythmGood,
      rhythmMiss: this.rhythmMiss,
      bestCombo: this.bestCombo,
      hits: this.hits,
      missedMajorPickups,
      comboBreaks: this.comboBreaks,
      offBeatPickups: this.offBeatPickups,
      collectedIsotypes: this.collectedIsotypes,
      totalIsotypes: TOTAL_SYNC_ISOTYPES,
      collectedIsotypePercent,
      beatSyncPercent,
      rankId: rankResult.rank.id,
      rankName: rankResult.rank.name,
      rankPhrase: rankResult.rank.phrase,
      rankProgressText: rankResult.progressText,
      rankReasonText: rankResult.reasonText,
      fenrirBlocked: rankResult.fenrirBlocked,
      unfinishedRankCap: rankResult.unfinishedCapApplied,
      rewardTitle: rankResult.rank.name.toUpperCase(),
      rewardMedals: this.getRewardMedals(rankResult.rank.name, finishedRun, collectedIsotypePercent, beatSyncPercent),
      victory: finishedRun,
      breakdown: {
        survival: survivalScore,
        isotypes: isotypeScore,
        beatSync: beatSyncScore,
        flow: flowScore,
        pressure: pressureScore,
        finish: finishScore,
        penalty: penaltyScore
      }
    };
  }

  finalize(elapsed: number, distance: number, phase: GamePhase, songEnergyRatio = 0) {
    this.finalized = this.calculate(elapsed, distance, phase, songEnergyRatio);
    return this.finalized;
  }

  getStats() {
    return {
      pickups: this.pickups,
      songAliveSeconds: this.songAliveSeconds,
      rhythmPerfect: this.rhythmPerfect,
      rhythmGood: this.rhythmGood,
      rhythmMiss: this.rhythmMiss,
      bestCombo: this.bestCombo
    };
  }

  private getBeatWeight(absDelta: number) {
    if (absDelta <= 0.1) return 1;
    if (absDelta <= 0.2) return 0.75;
    if (absDelta <= 0.35) return 0.35;
    return 0;
  }

  private calculatePenaltyScore(elapsed: number) {
    const missedMajorPickupPenalty = this.calculateMissedMajorPickups(elapsed, false) * 1800;
    const rawPenalty = this.hits * 2200 + missedMajorPickupPenalty + this.comboBreaks * 700 + this.offBeatPickups * 450;
    return Math.min(rawPenalty, WOLF_RANK_SCORE.maxPenalty);
  }

  private calculateMissedMajorPickups(elapsed: number, finishedRun: boolean) {
    const dueMajorPickups = MAJOR_SYNC_EVENTS.reduce((total, event) => {
      const songTime = RUN_CAMPEON_SONG_SYNC_CONFIG.gameDurationSeconds - event.pickupCounterTime;
      if (!finishedRun && songTime > elapsed) return total;
      return total + Math.max(1, event.count);
    }, 0);
    return Math.max(0, dueMajorPickups - this.collectedMajorPickups);
  }

  private isMajorPickup(intensity?: string) {
    return intensity === "high" || intensity === "max";
  }

  private getRewardMedals(rankName: string, finishedRun: boolean, isotypePercent: number, beatSyncPercent: number) {
    const medals = [`RANGO ${rankName.toUpperCase()}`];
    if (finishedRun) medals.push("ESCAPE");
    if (this.bestCombo >= 12) medals.push("AURA x12");
    else if (this.bestCombo >= 8) medals.push("AURA x8");
    else if (this.bestCombo >= 4) medals.push("CADENA x4");
    if (beatSyncPercent >= 92) medals.push("SINCRONIA 92%");
    if (isotypePercent >= 95) medals.push("ISOTIPOS 95%");
    return medals.slice(0, 5);
  }
}
